import React, { useState, useMemo } from 'react';
import { useStreamingProviders } from '../../shared/hooks/useStreamingProvider';
import { useStreamingOffer } from '../../shared/hooks/useStreamingOffer';
import { CircularProgress } from '@mui/material';
import { getBestCombi } from '../../../services/util/bc/getBestCombi';
import ErrorSnackbar from '../../shared/snackbar/ErrorSnackbar';
import BCProvider from './provider/BCProvider';
import BCCompetitions from './competition/BCCompetitions';
import BCCoveredGames from './coverage/BCCoveredGames';
import BCFilter from './filter/BCFilter';
import { useSelectedGamesForBCResult } from '../../../zustand/GamesFromSelectedTeams';
import { StyledTypo } from '../../overviewandfilter/styled/OverviewAndFilter.styled';
import Loader from '../../shared/loader/Loader';
import { StyledBox } from './BestCombi.styled';
import { LiveOrHighlightOrBoth } from './providerXcompetitonsXgames/types/HighlightOrLiveOrBoth.types';
import { StyledBackdrop } from '../../favouriteselection/search/Search.styled';
import GameFinderSection from '../../home/sections/GamefinderSection';
import RenderCoveredGames from './coverage/renderCoveredGames';

type BestCombiResultProps = {
  isPreview?: boolean; // isPreview => used by Home / Sneak Preview
};

const BestCombiResult: React.FC<BestCombiResultProps> = ({ isPreview }) => {
  const { gamesFromSelectedTeams } = useSelectedGamesForBCResult();
  const {
    providers: streamingPackages,
    loading: providerLoading,
    error: providerError,
  } = useStreamingProviders();
  const {
    streamingOffer: streamingOffers,
    loading: streamingOfferLoading,
    error: streamingOfferError,
  } = useStreamingOffer();

  const [mode, setMode] = useState<LiveOrHighlightOrBoth>('Default');

  const isError = providerError || streamingOfferError;
  const isLoading = providerLoading || streamingOfferLoading;

  const competitions = useMemo(() => {
    return Array.from(
      new Set(gamesFromSelectedTeams.map((game) => game.tournament_name))
    ).sort((a, b) => a.localeCompare(b));
  }, [gamesFromSelectedTeams]);

  const liveCombi = useMemo(() => {
    return getBestCombi(
      streamingPackages,
      gamesFromSelectedTeams,
      streamingOffers,
      'Live'
    );
  }, [streamingPackages, gamesFromSelectedTeams, streamingOffers]);

  const highlightCombi = useMemo(() => {
    return getBestCombi(
      streamingPackages,
      gamesFromSelectedTeams,
      streamingOffers,
      'Highlight'
    );
  }, [streamingPackages, gamesFromSelectedTeams, streamingOffers]);

  const totalCoveredGames = useMemo(
    () => Object.values(liveCombi.coveredGames).flat(),
    [liveCombi.coveredGames]
  );

  const totalCoveredGames_HIGHLIGHT = useMemo(
    () => Object.values(highlightCombi.coveredGames).flat(),
    [highlightCombi.coveredGames]
  );

  const handleModeChange = (selectedMode: LiveOrHighlightOrBoth) => {
    setMode(selectedMode);
  };

  if (isError) {
    return (
      <ErrorSnackbar
        open={true}
        customError="Error loading data! Please reload"
      />
    );
  }

  return (
    <>
      {!isPreview ? (
        <>
          {isLoading && (
            <StyledBackdrop open={isLoading}>
              <CircularProgress color="inherit" />
            </StyledBackdrop>
          )}
          <StyledTypo
            variant="h4"
            fontWeight="bold"
            fontStyle="italic"
            marginLeft={4}
          >
            Best combination
          </StyledTypo>
          <StyledBox>
            <BCFilter handleModeChange={handleModeChange} mode={mode} />
            <BCProvider
              providers={liveCombi.providers}
              streamingOffers={streamingOffers}
              gamesFromSelectedTeams={gamesFromSelectedTeams}
              competitions={competitions}
              streamingPackages={streamingPackages}
              mode={mode}
            />
            <BCCompetitions
              competitions={competitions}
              gamesFromSelectedTeams={gamesFromSelectedTeams}
              totalCoveredGames={totalCoveredGames}
              totalCoveredGames_HIGHLIGHT={totalCoveredGames_HIGHLIGHT}
              mode={mode}
            />
            <RenderCoveredGames
              mode={mode}
              liveCombi={{
                providers: liveCombi.providers,
                coveredGames: liveCombi.coveredGames,
                uncoveredGames: liveCombi.uncoveredGames,
                providerPackageCoverage: liveCombi.providerPackageCoverage,
              }}
              highlightCombi={{
                providers: highlightCombi.providers,
                coveredGames: highlightCombi.coveredGames,
                uncoveredGames: highlightCombi.uncoveredGames,
                providerPackageCoverage: highlightCombi.providerPackageCoverage,
              }}
              streamingPackages={streamingPackages}
              gamesFromSelectedTeams={gamesFromSelectedTeams}
              totalCoveredGames={totalCoveredGames}
              totalCoveredGames_HIGHLIGHT={totalCoveredGames_HIGHLIGHT}
            />

            <GameFinderSection callType="bc" />
          </StyledBox>
        </>
      ) : (
        // Preview used by Home
        <>
          {isLoading ? (
            <Loader />
          ) : (
            <BCProvider
              isPreview
              providers={liveCombi.providers}
              streamingOffers={streamingOffers}
              gamesFromSelectedTeams={gamesFromSelectedTeams}
              competitions={competitions}
              streamingPackages={streamingPackages}
              mode={mode}
            />
          )}
        </>
      )}
    </>
  );
};

export default BestCombiResult;
